import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, SendHorizonal, Loader2, BookOpen, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  'How do I onboard a new customer?',
  'What does the Signed status mean on a credit request?',
  'How do I send an email campaign without it going to spam?',
  'Why is my margin lower than the rate difference?',
];

/**
 * Guide-grounded AI help chat. Answers stream over SSE from /api/help/ask and
 * always end with the guides they were drawn from, so nothing is invented.
 */
export const HelpAssistant = ({ compact = false }) => {
  const { token } = useAuth();
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState([]); // {role, content, citations?}
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  const ask = useCallback(async (raw) => {
    const q = (raw ?? question).trim();
    if (!q || busy) return;
    setQuestion('');
    setTurns((t) => [...t, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/help/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: q, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error('Assistant unavailable');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, '').trim();
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          if (evt.type === 'session') sessionRef.current = evt.session_id;
          if (evt.type === 'delta') {
            setTurns((t) => {
              const next = [...t];
              next[next.length - 1] = { role: 'assistant', content: next[next.length - 1].content + evt.content };
              return next;
            });
          }
          if (evt.type === 'error') {
            setTurns((t) => {
              const next = [...t];
              next[next.length - 1] = { role: 'assistant', content: `Sorry — ${evt.message}` };
              return next;
            });
          }
          if (evt.type === 'done') {
            setTurns((t) => {
              const next = [...t];
              next[next.length - 1] = { ...next[next.length - 1], citations: evt.citations || [] };
              return next;
            });
          }
        }
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch (e) {
      setTurns((t) => {
        const next = [...t];
        next[next.length - 1] = { role: 'assistant', content: 'The assistant is unavailable right now. The guides below still have everything.' };
        return next;
      });
    } finally {
      setBusy(false);
    }
  }, [question, busy, token]);

  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white" data-testid="help-assistant">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-slate-900 text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Ask the TMS</p>
          <p className="text-[11px] text-slate-500">Answers come only from these guides</p>
        </div>
        {turns.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 px-2 text-[11px]"
            onClick={() => { setTurns([]); sessionRef.current = null; }}
            data-testid="help-assistant-reset"
          >
            <RotateCcw className="mr-1 h-3 w-3" /> New chat
          </Button>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className={`flex-1 space-y-4 overflow-y-auto px-4 py-4 ${compact ? 'max-h-[52vh]' : 'min-h-[240px] max-h-[46vh]'}`}
        data-testid="help-assistant-thread"
      >
        {turns.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Ask anything about using the TMS — how to do something, what a status means, or where a screen lives.</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
                data-testid={`help-suggestion-${s.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {s}
              </button>
            ))}
          </div>
        ) : (
          turns.map((t, i) => (
            <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
              <div
                className={`inline-block max-w-[92%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-left text-sm leading-6 ${
                  t.role === 'user' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-800'
                }`}
                data-testid={t.role === 'user' ? 'help-turn-user' : 'help-turn-assistant'}
              >
                {t.content || (busy && i === turns.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : null)}
              </div>
              {t.citations && t.citations.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="help-turn-citations">
                  {t.citations.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/help/${c.slug}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900"
                    >
                      <BookOpen className="h-3 w-3" /> {c.title}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      <form
        className="flex items-center gap-2 border-t border-slate-200 p-3"
        onSubmit={(e) => { e.preventDefault(); ask(); }}
      >
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="How do I…?"
          className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          data-testid="help-assistant-input"
        />
        <Button type="submit" size="sm" disabled={busy || !question.trim()} data-testid="help-assistant-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
};

export default HelpAssistant;
